import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { reportErrorToUser } from 'src/api/axiosClient/errorHandling';
import { getApiErrorMessage } from 'src/api/axiosClient/errorParser';
import { useChangeSubscriptionPlanApi, useGetSubscriptionPlanChangePreviewApi } from '../api';
import { ChangePlanDialogProps, ChangePlanDialogViewModel, SubscriptionPlanChangePreview } from '../types';

export const PLAN_CHANGE_COMPENSATION_REASON = 'Compensation for plan change';

const leavesCharge = (preview: SubscriptionPlanChangePreview) => preview.overageCreditsCharged > 0;

// The plan invoice is confirmed by the button that names it, so a fresh estimate that adds or drops
// it has to be shown before anything is changed
const planInvoiceChanged = (
  shown: SubscriptionPlanChangePreview | undefined,
  fresh: SubscriptionPlanChangePreview,
) => fresh.planPriceInvoiced !== shown?.planPriceInvoiced;

const notifyEstimateChanged = () => toast('The estimate has changed, review it and confirm again');

export const useChangePlanDialogController = (
  props: ChangePlanDialogProps,
): ChangePlanDialogViewModel => {
  const { subscription, plans, company, onClose, onChanged, onFreeCreditsAdjusted } = props;
  const getPreview = useGetSubscriptionPlanChangePreviewApi();
  const changePlan = useChangeSubscriptionPlanApi();

  const [selectedPlanId, setSelectedPlanId] = useState<string>();
  const [preview, setPreview] = useState<SubscriptionPlanChangePreview>();
  const [previewLoading, setPreviewLoading] = useState(false);
  // The dialog cannot be closed while set, so nothing awaited here outlives the selection it acts on
  const [submitting, setSubmitting] = useState(false);
  const [isOverageConfirmOpen, setOverageConfirmOpen] = useState(false);
  const [isAdjustFreeCreditsOpen, setAdjustFreeCreditsOpen] = useState(false);
  // Only the answer for the latest selection may land, the plan can be switched before it arrives
  const previewRequest = useRef(0);

  const current = subscription?.plan;
  const selectedPlan = plans.find(p => p.id === selectedPlanId);

  const reset = () => {
    previewRequest.current++;
    setSelectedPlanId(undefined);
    setPreview(undefined);
    setPreviewLoading(false);
    setSubmitting(false);
    setOverageConfirmOpen(false);
    setAdjustFreeCreditsOpen(false);
  };

  useEffect(reset, [subscription?.id]);

  const loadPreview = async (planId: string): Promise<SubscriptionPlanChangePreview | undefined> => {
    if (!subscription) return undefined;
    const request = ++previewRequest.current;
    setPreviewLoading(true);
    try {
      const response = await getPreview(subscription.id, planId);
      if (request !== previewRequest.current) return undefined;
      setPreview(response.data);
      return response.data;
    } catch (e) {
      if (request === previewRequest.current) {
        reportErrorToUser(e, getApiErrorMessage(e, 'Failed to estimate the plan change'));
      }
      return undefined;
    } finally {
      if (request === previewRequest.current) setPreviewLoading(false);
    }
  };

  const onSelectPlan = (planId: string) => {
    setSelectedPlanId(planId);
    setPreview(undefined);
    void loadPreview(planId);
  };

  const close = () => {
    reset();
    onClose();
  };

  const applyChange = async () => {
    if (!subscription || !selectedPlanId) return;
    setSubmitting(true);
    try {
      await changePlan(subscription.id, selectedPlanId);
    } catch (e) {
      reportErrorToUser(e, getApiErrorMessage(e, 'Failed to change the plan'));
      setSubmitting(false);
      return;
    }
    toast.success('Plan changed');
    close();
    await onChanged();
  };

  // Usage keeps running while the dialog is open, so the decision is taken on a fresh estimate
  const submit = async () => {
    if (!selectedPlan) return;
    const fresh = await loadPreview(selectedPlan.id);
    if (!fresh) return;
    if (planInvoiceChanged(preview, fresh)) {
      notifyEstimateChanged();
      return;
    }
    if (leavesCharge(fresh)) {
      setOverageConfirmOpen(true);
      return;
    }
    await applyChange();
  };

  // Free credits go in before the plan changes: the overage run may invoice the deficit as soon as
  // the change lands, and only the balance it finds then is spent
  const onFreeCreditsAdded = async () => {
    setSubmitting(true);
    let fresh: SubscriptionPlanChangePreview | undefined;
    try {
      // The credits are in whatever happens next, so the company is refreshed either way
      await onFreeCreditsAdjusted();
      fresh = selectedPlan && (await loadPreview(selectedPlan.id));
    } finally {
      setSubmitting(false);
    }
    if (!fresh) return;
    if (planInvoiceChanged(preview, fresh)) {
      notifyEstimateChanged();
      return;
    }
    if (leavesCharge(fresh)) {
      toast('The free credits do not cover the overage yet');
      return;
    }
    await applyChange();
  };

  return {
    open: !!subscription,
    subscription,
    company,
    // The dates stay, so only plans of the same period fit
    plans: plans.filter(
      p => p.id !== current?.id && p.period === current?.period && p.duration === current?.duration,
    ),
    selectedPlan,
    preview,
    previewLoading,
    submitting,
    isOverageConfirmOpen,
    isAdjustFreeCreditsOpen,
    adjustFreeCreditsFormData: {
      freeCredits: preview?.freeCreditsNeeded ?? 0,
      reason: selectedPlan
        ? `${PLAN_CHANGE_COMPENSATION_REASON} to ${selectedPlan.name}`
        : PLAN_CHANGE_COMPENSATION_REASON,
    },
    onSelectPlan,
    onRetryPreview: () => {
      if (selectedPlanId) void loadPreview(selectedPlanId);
    },
    onBackFromOverageConfirm: () => setOverageConfirmOpen(false),
    onChangeWithOverage: applyChange,
    onAddFreeCreditsAndChange: () => setAdjustFreeCreditsOpen(true),
    onCloseAdjustFreeCredits: () => setAdjustFreeCreditsOpen(false),
    onFreeCreditsAdded,
    close,
    submit,
  };
};
